import React, { useEffect, useRef, useCallback, useState, useMemo } from "react"
import { useLocation, useParams } from "react-router-dom"
import PropTypes from "prop-types"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import { createMetisMenu, disposeMetisMenu } from "helpers/metisMenu"
import withRouter from "components/Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import { useDispatch } from "react-redux"
import usePermissionRefresh from "hooks/usePermissionRefresh"
import {
  useAssignedBrands,
  canViewBrandOrders,
  getBrandOrdersBasePath,
} from "hooks/useAssignedBrands"
import { showActiveLink } from "store/reducers/sharedReducer"
import { hasPlanSocialLoginAccess } from "utils/planSocialLogins"
import useTenantPosConnections from "hooks/useTenantPosConnections"

const isAnyReportSubPermissionEnabled = reportSubs => {
  if (reportSubs === true) return true
  if (!reportSubs || typeof reportSubs !== "object") return false
  return Object.values(reportSubs).some(sub => sub?.isEnabled)
}

const buildReportSidebarFlags = (role, accType) => {
  const reportViewSubPermissions = getSubPermissions(role, 16, "report_1", accType)
  return {
    reportViewSubPermissions,
    isReportView: isAnyReportSubPermissionEnabled(reportViewSubPermissions),
  }
}

const SidebarContent = props => {
  const dispatch = useDispatch()
  const {
    isLoggedIn,
    userRole,
    accountType,
    accountScope,
    userIntegrations,
    planSocialLogins,
  } = useSelector(state => state.Login)
  const { activeLinkType } = useSelector(state => state.Shared)
  const { brands: assignedBrands } = useAssignedBrands()
  const { showLegacyPosApiKeyTab } = useTenantPosConnections()
  const ref = useRef()
  const metisMenuRef = useRef(null)
  const getDirectSubMenu = useCallback(parentNode => {
    if (!parentNode) return null
    return Array.from(parentNode.children || []).find(
      node =>
        node?.tagName?.toLowerCase() === "ul" &&
        node.classList?.contains("sub-menu")
    )
  }, [])
  // Prefer real leaf links — React Router can resolve toggle `to="/#"` oddly,
  // and has-arrow parents must never be treated as the active leaf.
  const queryLeafMenuLink = useCallback((ul, href) => {
    if (!ul || !href) return null
    const links = ul.querySelectorAll(`a[href="${href}"]`)
    for (let i = 0; i < links.length; i++) {
      if (!links[i].classList.contains("has-arrow")) return links[i]
    }
    return links[0] || null
  }, [])
  const handleMenuToggleClick = useCallback(event => {
    event.preventDefault()
  }, [])
  const openSubMenu = useCallback(subMenuEl => {
    if (!subMenuEl || subMenuEl.id === "side-menu") return
    subMenuEl.classList.add("mm-show")
    subMenuEl.classList.remove("mm-collapsing", "mm-collapsed")
    // Clear MetisMenu inline height so submenu is not stuck collapsed at 0px
    subMenuEl.style.height = ""
    subMenuEl.style.visibility = ""
    const parentLi = subMenuEl.parentElement
    const trigger = parentLi?.querySelector?.(":scope > a.has-arrow, :scope > a")
    if (trigger) {
      trigger.setAttribute("aria-expanded", "true")
      trigger.classList.remove("mm-collapsed")
    }
  }, [])

  const activateParentDropdown = useCallback(item => {
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = getDirectSubMenu(parent)

    if (parent2El && parent2El.id !== "side-menu") {
      openSubMenu(parent2El)
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        openSubMenu(parent2) // ul.sub-menu tag

        const parent3 = parent2.parentElement // li tag

        if (parent3) {
          parent3.classList.add("mm-active") // li
          if (parent3.firstElementChild) {
            parent3.firstElementChild.classList.add("mm-active") //a
          }
          const parent4 = parent3.parentElement // ul
          if (parent4) {
            openSubMenu(parent4) // ul
            const parent5 = parent4.parentElement
            if (parent5) {
              parent5.classList.add("mm-active")
              if (parent5.firstElementChild) {
                parent5.firstElementChild.classList.add("mm-active") // a tag
              }
            }
          }
        }
      }
      scrollElement(item)
      return false
    }
    scrollElement(item)
    return false
  }, [getDirectSubMenu, openSubMenu])

  const removeActivation = items => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i]
      const parent = items[i].parentElement

      if (item && item.classList.contains("active")) {
        item.classList.remove("active")
      }
      if (parent) {
        const parent2El = getDirectSubMenu(parent)
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show")
        }

        parent.classList.remove("mm-active")
        const parent2 = parent.parentElement

        if (parent2) {
          parent2.classList.remove("mm-show")

          const parent3 = parent2.parentElement
          if (parent3) {
            parent3.classList.remove("mm-active") // li
            if (parent3.firstElementChild) {
              parent3.firstElementChild.classList.remove("mm-active")
            }

            const parent4 = parent3.parentElement // ul
            if (parent4) {
              parent4.classList.remove("mm-show") // ul
              const parent5 = parent4.parentElement
              if (parent5) {
                parent5.classList.remove("mm-show") // li
                if (parent5.firstElementChild) {
                  parent5.firstElementChild.classList.remove("mm-active") // a tag
                }
              }
            }
          }
        }
      }
    }
  }

  const path = useLocation()
  const normalizedPathname = path.pathname.replace(/\/+$/, "") || "/"
  const isModifierGroupsActive =
    normalizedPathname.startsWith("/menus/modifier-groups") ||
    normalizedPathname === "/menus/modifier-group-details" ||
    /\/menus\/categories\/[^/]+\/items\/[^/]+\/modifiers(\/add|\/edit)?$/.test(
      normalizedPathname
    )
  const isOrdersActive =
    normalizedPathname === "/orders" ||
    normalizedPathname.startsWith("/orders/") ||
    normalizedPathname === "/f&b-orders" ||
    normalizedPathname.startsWith("/f&b-orders/") ||
    normalizedPathname === "/b2c-orders" ||
    normalizedPathname.startsWith("/b2c-orders/")

  const resolveOrdersMenuItem = useCallback((ul, pathName) => {
    if (!ul) return null
    const normalizedPath = (pathName || "").replace(/\/+$/, "") || "/"
    const brandMatch = normalizedPath.match(/^\/orders\/([^/]+)/)
    const brandId = brandMatch?.[1]
    const links = ul.getElementsByTagName("a")
    const matchByPath = targetPath => {
      for (let i = 0; i < links.length; i++) {
        const link = links[i]
        if (link.classList.contains("has-arrow")) continue
        const href = link.getAttribute("href") || ""
        if (href === "#" || href === "/#" || href.endsWith("#")) continue
        const linkPath = (link.pathname || "").replace(/\/+$/, "") || "/"
        if (linkPath === targetPath) return link
      }
      return null
    }
    if (brandId && brandId !== "order-details") {
      const brandLink = matchByPath(getBrandOrdersBasePath(brandId))
      if (brandLink) return brandLink
    }
    return (
      matchByPath("/orders") ||
      matchByPath("/f&b-orders") ||
      matchByPath("/b2c-orders")
    )
  }, [])

  const activeMenu = useCallback(() => {
    const pathName = path.pathname
    const normalizedCurrentPath = pathName.replace(/\/+$/, "") || "/"
    let matchingMenuItem = null
    const ul = document.getElementById("side-menu")
    if (!ul) return
    const items = ul.getElementsByTagName("a")

    // Reset full sidebar state first to avoid stale highlighted parents/icons.
    ul.querySelectorAll("a.active, a.mm-active").forEach(node => {
      node.classList.remove("active")
      node.classList.remove("mm-active")
    })
    ul.querySelectorAll("li.mm-active").forEach(node => {
      node.classList.remove("mm-active")
    })
    ul.querySelectorAll("ul.mm-show, ul.sub-menu").forEach(node => {
      node.classList.remove("mm-show", "mm-collapsing")
      node.style.height = ""
      node.style.visibility = ""
    })

    // Pathname wins over stale activeLinkType (e.g. navigating Customer → Order details).
    const isOrdersRoute =
      normalizedCurrentPath === "/orders" ||
      normalizedCurrentPath.startsWith("/orders/") ||
      normalizedCurrentPath === "/f&b-orders" ||
      normalizedCurrentPath.startsWith("/f&b-orders/") ||
      normalizedCurrentPath === "/b2c-orders" ||
      normalizedCurrentPath.startsWith("/b2c-orders/")

    if (isOrdersRoute) {
      const ordersMenuItem = resolveOrdersMenuItem(ul, normalizedCurrentPath)
      if (ordersMenuItem) {
        activateParentDropdown(ordersMenuItem)
        return
      }
    }

    const isItemsRoute =
      normalizedCurrentPath === "/menus/items" ||
      normalizedCurrentPath.startsWith("/menus/items/") ||
      (/^\/menus\/categories\/[^/]+\/items(\/|$)/.test(normalizedCurrentPath) &&
        !/\/modifiers(\/|$)/.test(normalizedCurrentPath))

    if (isItemsRoute) {
      const itemsMenuItem = queryLeafMenuLink(ul, "/menus/items")
      if (itemsMenuItem) {
        activateParentDropdown(itemsMenuItem)
        ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(openSubMenu)
        return
      }
    }

    // Menu setup edit/view use singular /menu/... while the sidebar leaf is /menus/menu-setup.
    if (
      normalizedCurrentPath.startsWith("/menus/menu-setup") ||
      normalizedCurrentPath.startsWith("/menu/menu-setup")
    ) {
      const menuSetupItem = queryLeafMenuLink(ul, "/menus/menu-setup")
      if (menuSetupItem) {
        activateParentDropdown(menuSetupItem)
        ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(openSubMenu)
        return
      }
    }

    // Gift Card Deep Link nested Campaign pages do not share the sidebar href prefix.
    if (
      normalizedCurrentPath === "/Giftcard-Deep-Link" ||
      normalizedCurrentPath.startsWith("/Giftcard-Deep-Link/") ||
      normalizedCurrentPath.startsWith("/Campaign/")
    ) {
      const deepLinkItem = queryLeafMenuLink(ul, "/Giftcard-Deep-Link")
      if (deepLinkItem) {
        activateParentDropdown(deepLinkItem)
        ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(openSubMenu)
        return
      }
    }

    // Coupons-group nested paths must not activate the Coupons leaf.
    if (
      normalizedCurrentPath === "/coupons-group" ||
      normalizedCurrentPath.startsWith("/coupons-group/")
    ) {
      const couponsGroupItem = queryLeafMenuLink(ul, "/coupons-group")
      if (couponsGroupItem) {
        activateParentDropdown(couponsGroupItem)
        ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(openSubMenu)
        return
      }
    }

    if (normalizedCurrentPath.startsWith("/billing")) {
      const billingHrefs = ["/billing/invoices", "/billing/plans", "/billing"]
      let billingMenuItem = null
      for (let i = 0; i < billingHrefs.length; i++) {
        const href = billingHrefs[i]
        if (
          normalizedCurrentPath === href ||
          normalizedCurrentPath.startsWith(`${href}/`)
        ) {
          billingMenuItem = queryLeafMenuLink(ul, href)
          if (billingMenuItem) break
        }
      }
      if (billingMenuItem) {
        activateParentDropdown(billingMenuItem)
        ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(openSubMenu)
        return
      }
    }

    for (let i = 0; i < items.length; ++i) {
      const item = items[i]
      const href = item.getAttribute("href") || ""
      const isToggleLink = item.classList.contains("has-arrow")
      const isHashLink =
        href === "#" ||
        href === "/#" ||
        href.startsWith("#") ||
        href.endsWith("#")
      if (isToggleLink || isHashLink) continue

      const normalizedItemPath = (item.pathname || "").replace(/\/+$/, "") || "/"
      if (normalizedCurrentPath === normalizedItemPath) {
        matchingMenuItem = items[i]
        break
      }
      // Longest prefix match for detail routes (e.g. /orders/:brandId/order-details)
      if (
        normalizedItemPath !== "/" &&
        normalizedCurrentPath.startsWith(`${normalizedItemPath}/`)
      ) {
        if (
          !matchingMenuItem ||
          normalizedItemPath.length >
            ((matchingMenuItem.pathname || "").replace(/\/+$/, "") || "/").length
        ) {
          matchingMenuItem = items[i]
        }
      }
    }
    if (activeLinkType === "CATEGORIES") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/categories")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ITEMS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/items")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "MODIFIER_GROUPS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/modifier-groups")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "MODIFIERS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/modifiers")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ITEM_TAGS") {
      const itemTagsMenuItem = queryLeafMenuLink(ul, "/menus/item-tags")
      if (itemTagsMenuItem) {
        activateParentDropdown(itemTagsMenuItem)
      }
    } else if (activeLinkType === "USER_ACTIVITY") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/user-activity")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "LOCATION") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/branches")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "USER") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/users")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "CUSTOMER") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/customers")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "DELETED_CUSTOMER") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/customers/deleted-customers")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "CUSTOMER_LOYALTY") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/customers/loyalty")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ROLES") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/roles")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "BANNER") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/banners")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "OFFER_BANNER") {
      const offerBannerMenuItem = queryLeafMenuLink(ul, "/offer-banners")
      if (offerBannerMenuItem) {
        activateParentDropdown(offerBannerMenuItem)
      }
    } else if (activeLinkType === "F&B_ORDER") {
      const ordersMenuItem = resolveOrdersMenuItem(ul, normalizedCurrentPath)
      if (ordersMenuItem) {
        activateParentDropdown(ordersMenuItem)
      }
    } else if (activeLinkType === "B2C_ORDER") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/b2c-orders")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "API_LOGS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/api-logs")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "RATINGS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/ratings")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "PROMOTION") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/discounts")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "COUPON") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/coupons")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "COUPONS_GROUP") {
      const couponsGroupMenuItem = queryLeafMenuLink(ul, "/coupons-group")
      if (couponsGroupMenuItem) {
        activateParentDropdown(couponsGroupMenuItem)
      }
    } else if (activeLinkType === "COUPON_USAGE") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/coupon-usage")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "COUPON_REDEEM") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/wallet-coupons")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "COUNTRY") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/countries")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "REGION") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/regions")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ZONE") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/zones")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "CITY") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/cities")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "CUSTOMER_SUPPORT") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/support-issues")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "TICKET_DETAILS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/support-tickets")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "INGREDIENTS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/ingredients")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ALLERGEN") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/allergens")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "STATIC_MAINTENANCE") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/static-maintenance")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "APP_NOTIFICATIONS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/app-notifications")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "BOOKINGS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/bookings")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "COMBOS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/combos")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "FAQ") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/faq")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "RATING_QUESTION") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/rating-questions")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "BRANCH_RATING") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/branch-ratings")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "ITEM_GROUPS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/menus/item-groups")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "POS") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/branch-sync")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "VEHICLE") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/vehicles")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "GIFT_CARD") {
      const categoriesMenuItem = queryLeafMenuLink(ul, "/giftcard")
      if (categoriesMenuItem) {
        activateParentDropdown(categoriesMenuItem)
      }
    } else if (activeLinkType === "GIFT_CARD_DEEP_LINK") {
      const deepLinkMenuItem = queryLeafMenuLink(ul, "/Giftcard-Deep-Link")
      if (deepLinkMenuItem) {
        activateParentDropdown(deepLinkMenuItem)
      }
    } else if (activeLinkType === "MENU_SETUP") {
      const menuSetupMenuItem = queryLeafMenuLink(ul, "/menus/menu-setup")
      if (menuSetupMenuItem) {
        activateParentDropdown(menuSetupMenuItem)
      }
    } else if (activeLinkType === "BILLING") {
      const billingHrefs = ["/billing/invoices", "/billing/plans", "/billing"]
      let billingMenuItem = null
      for (let i = 0; i < billingHrefs.length; i++) {
        const href = billingHrefs[i]
        if (
          normalizedCurrentPath === href ||
          normalizedCurrentPath.startsWith(`${href}/`)
        ) {
          billingMenuItem = queryLeafMenuLink(ul, href)
          if (billingMenuItem) break
        }
      }
      if (billingMenuItem) {
        activateParentDropdown(billingMenuItem)
      }
    } else if (activeLinkType === "INTEGRATIONS") {
      const integrationsMenuItem = queryLeafMenuLink(ul, "/integrations")
      if (integrationsMenuItem) {
        activateParentDropdown(integrationsMenuItem)
      }
    }
    if (matchingMenuItem && !activeLinkType) {
      activateParentDropdown(matchingMenuItem)
    }

    // Keep parent accordion open after leaf navigation (Items / Item Tags / etc.).
    // MetisMenu can leave inline height:0 which would visually collapse the dropdown.
    ul.querySelectorAll("li.mm-active > ul.sub-menu").forEach(node => {
      openSubMenu(node)
    })
  }, [
    path.pathname,
    activateParentDropdown,
    activeLinkType,
    resolveOrdersMenuItem,
    openSubMenu,
    queryLeafMenuLink,
  ])
  // Latest activeMenu for the MetisMenu effect, which outlives route changes.
  const activeMenuRef = useRef(activeMenu)
  activeMenuRef.current = activeMenu

  useEffect(() => {
    ref.current.recalculate()
  }, [])

  const {
    categoryId,
    itemId,
    modifierOptionId,
    modifierItemId,
    bannerId,
    couponGroupId,
    couponUsageId,
    comboId,
  } = useParams()
  {
  }
  useEffect(() => {
    const pathname = path.pathname
    const normalizedPathname = pathname.replace(/\/+$/, "") || "/"
    const isModifierGroupsRoute =
      normalizedPathname.endsWith("/menus/modifier-groups") ||
      normalizedPathname.endsWith("/menus/modifier-groups/add") ||
      normalizedPathname.endsWith("/menus/modifier-groups/edit") ||
      normalizedPathname.endsWith("/menus/modifier-group-details") ||
      /\/menus\/categories\/[^/]+\/items\/[^/]+\/modifiers(\/add|\/edit)?$/.test(
        normalizedPathname
      )
    if (
      pathname === "/menus/categories/add" ||
      pathname === `/menus/categories/category-details` ||
      pathname === "/menus/categories/edit"
    ) {
      dispatch(showActiveLink("CATEGORIES"))
    } else if (pathname === "/customers/deleted-customer-details") {
      dispatch(showActiveLink("DELETED_CUSTOMER"))
    } else if (
      pathname === "/menus/items/add" ||
      pathname === "/menus/items/item-details" ||
      pathname === `/menus/categories/${categoryId}/items` ||
      pathname === `/menus/categories/${categoryId}/items/add` ||
      pathname === `/menus/categories/${categoryId}/items/${itemId}/edit-item`
    ) {
      dispatch(showActiveLink("ITEMS"))
    } else if (isModifierGroupsRoute) {
      dispatch(showActiveLink("MODIFIER_GROUPS"))
    } else if (
      pathname === "/menus/modifiers/add" ||
      pathname === "/menus/modifiers/edit" ||
      pathname === "/menus/modifiers/modifier-details" ||
      pathname === `/menus/modifiers/${modifierOptionId}/items` ||
      pathname ===
      `/menus/categories/${categoryId}/items/${itemId}/modifiers/${modifierOptionId}/items` ||
      pathname ===
      `/menus/categories/${categoryId}/items/${itemId}/modifiers/${modifierOptionId}/items/add` ||
      pathname ===
      `/menus/categories/${categoryId}/items/${itemId}/modifiers/${modifierOptionId}/items/${modifierItemId}` ||
      pathname ===
      `/menus/categories/${categoryId}/items/${itemId}/modifiers/${modifierOptionId}/items/edit`
    ) {
      dispatch(showActiveLink("MODIFIERS"))
    } else if (
      pathname === "/menus/combos/add" ||
      pathname === "/menus/combos/edit" ||
      pathname === "/menus/combos/combo-details" ||
      pathname === `/menus/combos/${comboId}/items`
    ) {
      dispatch(showActiveLink("COMBOS"))
    } else if (
      pathname === "/menus/item-groups/add" ||
      pathname === "/menus/item-groups/edit" ||
      pathname === "/menus/item-groups/item-group-details"
    ) {
      dispatch(showActiveLink("ITEM_GROUPS"))
    } else if (pathname === "/user-activity/details") {
      dispatch(showActiveLink("USER_ACTIVITY"))
    } else if (
      pathname === "/branches/add" ||
      pathname === "/branches/edit" ||
      pathname === "/branches/branch-details"
    ) {
      dispatch(showActiveLink("LOCATION"))
    } else if (
      pathname === "/users/add" ||
      pathname === "/users/user-details" ||
      pathname === "/users/edit"
    ) {
      dispatch(showActiveLink("USER"))
    } else if (
      pathname === "/customers/customer-details" ||
      pathname === "/customers/edit"
    ) {
      dispatch(showActiveLink("CUSTOMER"))
    } else if (pathname === "/customers/loyalty/add") {
      dispatch(showActiveLink("CUSTOMER_LOYALTY"))
    } else if (pathname === "/roles/add" || pathname === "/roles/edit") {
      dispatch(showActiveLink("ROLES"))
    } else if (
      pathname === "/banners" ||
      pathname === `/banners/${bannerId}/slides/add` ||
      pathname === `/banners/${bannerId}/slides/edit`
    ) {
      dispatch(showActiveLink("BANNER"))
    } else if (
      pathname === "/offer-banners" ||
      pathname === `/offer-banners/${bannerId}/slides/add` ||
      pathname === `/offer-banners/${bannerId}/slides/edit`
    ) {
      dispatch(showActiveLink("OFFER_BANNER"))
    } else if (
      pathname === "/f&b-orders" ||
      pathname === "/f&b-orders/order-details" ||
      pathname === "/orders" ||
      pathname.startsWith("/orders/")
    ) {
      dispatch(showActiveLink("F&B_ORDER"))
    } else if (
      pathname === "/b2c-orders" ||
      pathname === "/b2c-orders/order-details"
    ) {
      dispatch(showActiveLink("B2C_ORDER"))
    } else if (pathname === "/api-logs/api-logs-details") {
      dispatch(showActiveLink("API_LOGS"))
    } else if (pathname === "/ratings/rating-details") {
      dispatch(showActiveLink("RATINGS"))
    } else if (
      pathname === "/discounts/add" ||
      pathname === "/discounts/edit" ||
      pathname === "/discounts/discount-details"
    ) {
      dispatch(showActiveLink("PROMOTION"))
    } else if (
      pathname === "/coupons-group" ||
      pathname.startsWith("/coupons-group/")
    ) {
      dispatch(showActiveLink("COUPONS_GROUP"))
    } else if (
      pathname === "/coupons/coupon-details" ||
      pathname === "/coupons/add" ||
      pathname === "/coupons/edit"
    ) {
      dispatch(showActiveLink("COUPON"))
    } else if (
      pathname.startsWith("/menus/menu-setup") ||
      pathname.startsWith("/menu/menu-setup")
    ) {
      dispatch(showActiveLink("MENU_SETUP"))
    } else if (
      pathname === "/Giftcard-Deep-Link" ||
      pathname.startsWith("/Giftcard-Deep-Link/") ||
      pathname.startsWith("/Campaign/")
    ) {
      dispatch(showActiveLink("GIFT_CARD_DEEP_LINK"))
    } else if (
      pathname === "/wallet-coupons" ||
      pathname === "/wallet-coupons/coupon-details" ||
      pathname === "/wallet-coupons/add" ||
      pathname === "/wallet-coupons/edit"
    ) {
      dispatch(showActiveLink("COUPON_REDEEM"))
    } else if (
      pathname === `/coupon-usage` ||
      pathname === `/coupon-usage/${couponUsageId}` ||
      pathname === `/coupon-usage/details`
    ) {
      dispatch(showActiveLink("COUPON_USAGE"))
    } else if (
      pathname === `/countries/country-details` ||
      pathname === `/countries/add` ||
      pathname === `/countries/edit`
    ) {
      dispatch(showActiveLink("COUNTRY"))
    } else if (
      pathname === `/regions/region-details` ||
      pathname === `/regions/add` ||
      pathname === `/regions/edit`
    ) {
      dispatch(showActiveLink("REGION"))
    } else if (
      pathname === `/zones/zone-details` ||
      pathname === `/zones/add` ||
      pathname === `/zones/edit`
    ) {
      dispatch(showActiveLink("ZONE"))
    } else if (
      pathname === `/cities/city-details` ||
      pathname === `/cities/add` ||
      pathname === `/cities/edit`
    ) {
      dispatch(showActiveLink("CITY"))
    } else if (
      pathname === `/support-issues/issue-details` ||
      pathname === `/support-issues/add-issue` ||
      pathname === `/support-issues/edit-issue`
    ) {
      dispatch(showActiveLink("CUSTOMER_SUPPORT"))
    } else if (
      pathname === `/ingredients/add` ||
      pathname === `/ingredients/edit` ||
      pathname === `/ingredients/ingredient-details`
    ) {
      dispatch(showActiveLink("INGREDIENTS"))
    } else if (
      pathname === `/menus/item-tags/add` ||
      pathname === `/menus/item-tags/edit` ||
      pathname === `/menus/item-tags`
    ) {
      dispatch(showActiveLink("ITEM_TAGS"))
    } else if (
      pathname === `/allergens/add` ||
      pathname === `/allergens/edit` ||
      pathname === `/allergens/allergen-details`
    ) {
      dispatch(showActiveLink("ALLERGEN"))
    } else if (
      pathname === `/static-maintenance/add` ||
      pathname === `/static-maintenance/edit`
    ) {
      dispatch(showActiveLink("STATIC_MAINTENANCE"))
    } else if (pathname === `/support-tickets/ticket-details`) {
      dispatch(showActiveLink("TICKET_DETAILS"))
    } else if (pathname === `/app-notifications/edit`) {
      dispatch(showActiveLink("APP_NOTIFICATIONS"))
    } else if (pathname === `/bookings/booking-details`) {
      dispatch(showActiveLink("BOOKINGS"))
    } else if (pathname === `/faq/faq-details` || pathname === "/faq/add") {
      dispatch(showActiveLink("FAQ"))
    } else if (
      pathname === `/rating-questions/questions-details` ||
      pathname === `/rating-questions/add` ||
      pathname === `/rating-questions/edit`
    ) {
      dispatch(showActiveLink("RATING_QUESTION"))
    } else if (pathname === `/branch-ratings/branch-rating-details`) {
      dispatch(showActiveLink("BRANCH_RATING"))
    } else if (pathname === `/branch-sync/details`) {
      dispatch(showActiveLink("POS"))
    } else if (
      pathname === `/vehicles/add` ||
      pathname === `/vehicles/edit` ||
      pathname === "/vehicles/vehicle-details"
    ) {
      dispatch(showActiveLink("VEHICLE"))
    } else if (
      pathname === `/giftcard/add` ||
      pathname === `/giftcard/edit` ||
      pathname === "/giftcard/details"
    ) {
      dispatch(showActiveLink("GIFT_CARD"))
    } else if (pathname.startsWith("/billing")) {
      dispatch(showActiveLink("BILLING"))
    } else if (
      pathname === "/integrations" ||
      pathname.startsWith("/integrations/") ||
      pathname === "/settings/integrations" ||
      pathname.startsWith("/settings/integrations/")
    ) {
      dispatch(showActiveLink("INTEGRATIONS"))
    } else if (pathname.startsWith("/superadmin")) {
      dispatch(showActiveLink("SUPERADMIN"))
    } else {
      dispatch(showActiveLink(""))
    }
  }, [path.pathname])

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  const isIntegrationsSidebarVisible = useMemo(() => {
    if (
      isPermissionEnabled(userRole, 48, "tenant_integrations_1", accountType)
    ) {
      return true
    }
    if (
      Array.isArray(userIntegrations) &&
      userIntegrations.some(
        int => int?.permissions?.tenant_integrations_1 === true
      )
    ) {
      return true
    }
    const map = userRole?.integrationPermissions
    if (map && typeof map === "object" && !Array.isArray(map)) {
      return Object.values(map).some(
        entry =>
          entry &&
          typeof entry === "object" &&
          entry.tenant_integrations_1 === true
      )
    }
    return false
  }, [userRole, accountType, userIntegrations])

  const isSocialAuthSidebarVisible = useMemo(
    () =>
      isPermissionEnabled(userRole, 53, "social_auth_settings_1", accountType) &&
      hasPlanSocialLoginAccess(planSocialLogins),
    [userRole, accountType, planSocialLogins]
  )

  const userSideBarPermissions = useMemo(() => ({
    ...buildReportSidebarFlags(userRole, accountType),
    isDashboardEidiyahView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_5",
      accountType
    ),
    isDashboardSalesView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_1",
      accountType
    ),
    isDashboardBranchesView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_2",
      accountType
    ),
    isDashboardBookingsView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_3",
      accountType
    ),
    isDashboardRatingsView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_4",
      accountType
    ),
    isDashboardEstoreView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_6",
      accountType
    ),
    isDashboardMarketingView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_7",
      accountType
    ),
    isDashboardWalletView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_8",
      accountType
    ),
    isInAppContentView: isPermissionEnabled(
      userRole,
      2,
      "inAppContent_1",
      accountType
    ),
    isOfferBannerView: isPermissionEnabled(
      userRole,
      49,
      "offerBanner_1",
      accountType
    ),
    isRolesView: isPermissionEnabled(userRole, 3, "roles_1", accountType),
    isMenuView: getSubPermissions(userRole, 4, "menus_1", accountType),
    isLocationsView: isPermissionEnabled(
      userRole,
      5,
      "branchLocations_1",
      accountType
    ),
    isUsersView: isPermissionEnabled(
      userRole,
      6,
      "usersPermission_1",
      accountType
    ),
    isCustomersView: isPermissionEnabled(
      userRole,
      7,
      "customersPermission_1",
      accountType
    ),
    customerSubPermissions: getSubPermissions(
      userRole,
      7,
      "customersPermission_1",
      accountType
    ),
    isCustomersBlacklistView: isPermissionEnabled(
      userRole,
      7,
      "customersPermission_3",
      accountType
    ),
    isCustomersDeleted: isPermissionEnabled(
      userRole,
      7,
      "customersPermission_5",
      accountType
    ),
    isFeedbackView: isPermissionEnabled(userRole, 8, "feedback_1", accountType),
    isOrdersView: isPermissionEnabled(userRole, 9, "order_1", accountType),
    orderViewSubPermissions: getSubPermissions(
      userRole,
      9,
      "order_1",
      accountType
    ),
    orderEditSubPermissions: getSubPermissions(
      userRole,
      9,
      "order_2",
      accountType
    ),
    isCurbsidePickupView: isPermissionEnabled(
      userRole,
      10,
      "curbside_1",
      accountType
    ),
    isMenusView: isPermissionEnabled(userRole, 4, "menus_1", accountType),
    MenusSubPermissions: getSubPermissions(userRole, 4, "menus_1", accountType),
    MenusUpdateSubPermissions: getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    ),
    isCountriesView: isPermissionEnabled(
      userRole,
      11,
      "country_1",
      accountType
    ),
    isTaxView: isPermissionEnabled(userRole, 12, "tax_1", accountType),
    isApiLogsView: isPermissionEnabled(userRole, 13, "api_logs_1", accountType),
    isPromotionView: isPermissionEnabled(
      userRole,
      14,
      "promotion_1",
      accountType
    ),
    isCouponRedeemView: isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_1",
      accountType
    ),
    isNotificationView: isPermissionEnabled(
      userRole,
      15,
      "notification_1",
      accountType
    ),
    isEstoreReportView: isPermissionEnabled(
      userRole,
      45,
      "estore_report_1",
      accountType
    ),
    eStoreViewSubPermissions: getSubPermissions(
      userRole,
      45,
      "estore_report_1",
      accountType
    ),
    isCustomerSupportView: isPermissionEnabled(
      userRole,
      17,
      "customer_support_1",
      accountType
    ),
    isFaqView:
      isPermissionEnabled(userRole, 18, "faq_1", accountType) ||
      isPermissionEnabled(userRole, 54, "rider_faq_1", accountType),
    isReferralView: isPermissionEnabled(
      userRole,
      19,
      "referral_1",
      accountType
    ),
    isLoyaltyView: isPermissionEnabled(userRole, 20, "loyalty_1", accountType),
    isWalletSettingsView: isPermissionEnabled(
      userRole,
      60,
      "wallet_settings_1",
      accountType
    ),
    isBookingView: isPermissionEnabled(userRole, 21, "booking_1", accountType),
    isQrCodeView: isPermissionEnabled(userRole, 22, "qr_code_1", accountType),
    isIngredientView: isPermissionEnabled(
      userRole,
      23,
      "ingredient_1",
      accountType
    ),
    isAllergenView: isPermissionEnabled(
      userRole,
      24,
      "allergen_1",
      accountType
    ),
    isItemTagView: isPermissionEnabled(
      userRole,
      4,
      "item_tag_1",
      accountType
    ),
    isRegionView: isPermissionEnabled(userRole, 25, "region_1", accountType),
    isCityView: isPermissionEnabled(userRole, 26, "city_1", accountType),
    isZoneView: isPermissionEnabled(userRole, 43, "zone_1", accountType),
    isRatingView: isPermissionEnabled(userRole, 27, "rating_1", accountType),
    isAPIKeyView: isPermissionEnabled(userRole, 28, "api_key_1", accountType),
    isBranchSyncView: isPermissionEnabled(
      userRole,
      29,
      "branch_sync_1",
      accountType
    ),
    isMenuSyncView: isPermissionEnabled(
      userRole,
      30,
      "menu_sync_3",
      accountType
    ),
    isIntegrationsView: isPermissionEnabled(
      userRole,
      48,
      "tenant_integrations_1",
      accountType
    ),
    isSocialAuthSettingsView:
      isPermissionEnabled(userRole, 53, "social_auth_settings_1", accountType),
    isAddEmailView: isPermissionEnabled(
      userRole,
      31,
      "add_emails_1",
      accountType
    ),
    isAppSettingsView: isPermissionEnabled(
      userRole,
      32,
      "app_settings_1",
      accountType
    ),
    AppSettingsViewSubPermissions: getSubPermissions(
      userRole,
      32,
      "app_settings_1",
      accountType
    ),
    AppSettingsUpdateSubPermissions: getSubPermissions(
      userRole,
      32,
      "app_settings_3",
      accountType
    ),
    isOrderCancelSettingsView: isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_1",
      accountType
    ),
    isDeliverySettingsView: isPermissionEnabled(
      userRole,
      59,
      "delivery_settings_1",
      accountType
    ),
    isTermsConditionsView:
      isPermissionEnabled(userRole, 33, "term_condition_1", accountType) ||
      isPermissionEnabled(userRole, 55, "rider_term_condition_1", accountType),
    isPrivacyPolicyView:
      isPermissionEnabled(userRole, 34, "privacy_policy_1", accountType) ||
      isPermissionEnabled(userRole, 56, "rider_privacy_policy_1", accountType),
    isVehicleView: isPermissionEnabled(userRole, 35, "vehicle_1", accountType),
    isBrandsManagementView: isPermissionEnabled(
      userRole,
      52,
      "brands_1",
      accountType
    ),
    isDeliveryPartnersView: isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_1",
      accountType
    ),
    isDeliveryPartnerSettingsView: isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_6",
      accountType
    ),
    isBillingView: isPermissionEnabled(
      userRole,
      51,
      "billing_1",
      accountType
    ),
    isUserActivityView: isPermissionEnabled(
      userRole,
      36,
      "user_activity_1",
      accountType
    ),
    isGiftCardView: isPermissionEnabled(
      userRole,
      37,
      "gift_card_1",
      accountType
    ),
    isGiftCardUsageView: isPermissionEnabled(
      userRole,
      38,
      "gift_card_usage_1",
      accountType
    ),
    isDynamicLinkView: isPermissionEnabled(
      userRole,
      39,
      "dynamic_link_1",
      accountType
    ),
    isEidiyahSettingsView: isPermissionEnabled(
      userRole,
      40,
      "gift_card_settings_1",
      accountType
    ),
    isStaticMaintenanceView: isPermissionEnabled(
      userRole,
      41,
      "static_maintenance_1",
      accountType
    ),
  }), [userRole, accountType, accountScope])

  const visibleOrderBrands = useMemo(
    () =>
      assignedBrands.filter(brand =>
        canViewBrandOrders(brand, userSideBarPermissions.isOrdersView)
      ),
    [assignedBrands, userSideBarPermissions.isOrdersView]
  )

  const ordersMenuLayoutKey =
    visibleOrderBrands.length >= 2 ? "multi" : "single"

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [path.pathname])

  useEffect(() => {
    activeMenu()
  }, [path.pathname, path.search, activeLinkType])

  useEffect(() => {
    const sideMenu = document.getElementById("side-menu")
    if (!sideMenu) return undefined

    const disposeMenu = () => {
      disposeMetisMenu(metisMenuRef.current)
      metisMenuRef.current = null
    }

    const createMenu = () => {
      const menuEl = document.getElementById("side-menu")
      if (!menuEl) return
      disposeMenu()
      metisMenuRef.current = createMetisMenu(menuEl, { toggle: false })
    }

    let frameId = requestAnimationFrame(() => {
      createMenu()
      requestAnimationFrame(() => {
        activeMenuRef.current()
      })
    })

    // Menu items render from permissions / plan data that can arrive after this
    // effect; MetisMenu only binds the items present at init, so rebind when the
    // menu's items change.
    const observer = new MutationObserver(mutations => {
      const itemsChanged = mutations.some(
        m => m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)
      )
      if (!itemsChanged) return
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        createMenu()
        activeMenuRef.current()
      })
    })
    observer.observe(sideMenu, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frameId)
      disposeMenu()
    }
  }, [ordersMenuLayoutKey, visibleOrderBrands.length])

  // Collapsed rail: position label/submenu flyouts as fixed so vertical scroll can stay enabled.
  useEffect(() => {
    const sideMenu = document.getElementById("side-menu")
    if (!sideMenu) return undefined

    let activeLi = null

    const clearFlyoutStyles = li => {
      if (!li) return
      li.classList.remove("mm-collapsed-flyout-open")
      const sub = Array.from(li.children).find(
        node =>
          node?.tagName?.toLowerCase() === "ul" &&
          node.classList?.contains("sub-menu")
      )
      if (sub) {
        sub.style.removeProperty("top")
        sub.style.removeProperty("left")
        sub.style.removeProperty("right")
        sub.style.removeProperty("inset-inline-start")
        sub.style.removeProperty("inset-inline-end")
      }
      const label = li.querySelector(":scope > a > span")
      if (label) {
        label.style.removeProperty("top")
        label.style.removeProperty("left")
        label.style.removeProperty("right")
        label.style.removeProperty("inset-inline-start")
        label.style.removeProperty("inset-inline-end")
      }
    }

    const positionFlyout = li => {
      if (!li || !document.body.classList.contains("vertical-collpsed")) {
        return
      }
      const rect = li.getBoundingClientRect()
      const isRtl = document.documentElement.getAttribute("dir") === "rtl"
      const sub = Array.from(li.children).find(
        node =>
          node?.tagName?.toLowerCase() === "ul" &&
          node.classList?.contains("sub-menu")
      )
      li.classList.add("mm-collapsed-flyout-open")

      if (sub) {
        sub.style.top = `${Math.round(rect.top)}px`
        if (isRtl) {
          sub.style.right = `${Math.round(window.innerWidth - rect.left)}px`
          sub.style.left = "auto"
        } else {
          sub.style.left = `${Math.round(rect.right)}px`
          sub.style.right = "auto"
        }
        return
      }

      const label = li.querySelector(":scope > a > span")
      if (!label) return
      label.style.top = `${Math.round(rect.top)}px`
      if (isRtl) {
        label.style.right = `${Math.round(window.innerWidth - rect.left)}px`
        label.style.left = "auto"
      } else {
        label.style.left = `${Math.round(rect.right)}px`
        label.style.right = "auto"
      }
    }

    const onPointerOver = event => {
      const li = event.target?.closest?.("#side-menu > li")
      if (!li || !sideMenu.contains(li)) return
      if (li === activeLi) {
        positionFlyout(li)
        return
      }
      if (activeLi) clearFlyoutStyles(activeLi)
      activeLi = li
      positionFlyout(li)
    }

    const onPointerLeave = event => {
      if (!sideMenu.contains(event.relatedTarget)) {
        if (activeLi) clearFlyoutStyles(activeLi)
        activeLi = null
      }
    }

    const onScrollOrResize = () => {
      if (activeLi && document.body.classList.contains("vertical-collpsed")) {
        positionFlyout(activeLi)
      } else if (activeLi) {
        clearFlyoutStyles(activeLi)
        activeLi = null
      }
    }

    const scrollHost =
      sideMenu.closest(".simplebar-content-wrapper") ||
      sideMenu.closest("[data-simplebar]") ||
      null

    sideMenu.addEventListener("pointerover", onPointerOver)
    sideMenu.addEventListener("pointerleave", onPointerLeave)
    scrollHost?.addEventListener("scroll", onScrollOrResize, { passive: true })
    window.addEventListener("resize", onScrollOrResize)

    return () => {
      sideMenu.removeEventListener("pointerover", onPointerOver)
      sideMenu.removeEventListener("pointerleave", onPointerLeave)
      scrollHost?.removeEventListener("scroll", onScrollOrResize)
      window.removeEventListener("resize", onScrollOrResize)
      if (activeLi) clearFlyoutStyles(activeLi)
    }
  }, [ordersMenuLayoutKey, visibleOrderBrands.length])

  usePermissionRefresh()

  const showBillingNav = useMemo(() => {
    const noPlanTenant =
      Array.isArray(userRole?.permissions) && userRole.permissions.length === 0
    return (
      noPlanTenant ||
      isPermissionEnabled(userRole, 51, "billing_1", accountType)
    )
  }, [userRole, accountType])

  const isCouponsCreate = isPermissionEnabled(
    userRole,
    14,
    "promotion_2",
    accountType
  )

  const showOperationsSection =
    userSideBarPermissions.isOrdersView ||
    userSideBarPermissions.isBookingView ||
    userSideBarPermissions.isDeliveryPartnersView

  const showCatalogSection =
    userSideBarPermissions.isMenusView || userSideBarPermissions.isItemTagView

  const showCustomersSupportSection =
    userSideBarPermissions.isCustomersView ||
    userSideBarPermissions.isCustomersDeleted ||
    userSideBarPermissions.isCustomerSupportView ||
    userSideBarPermissions.isRatingView

  const showBannersOffers =
    userSideBarPermissions.isInAppContentView ||
    userSideBarPermissions.isOfferBannerView

  const showGiftCardSection =
    userSideBarPermissions.isGiftCardView ||
    userSideBarPermissions.isDynamicLinkView ||
    userSideBarPermissions.isGiftCardUsageView

  const showDiscountsCouponsSection =
    userSideBarPermissions.isPromotionView ||
    userSideBarPermissions.isCouponRedeemView

  const showMarketingSection =
    showBannersOffers ||
    showDiscountsCouponsSection ||
    showGiftCardSection ||
    userSideBarPermissions.isNotificationView ||
    userSideBarPermissions.isQrCodeView

  const showHelpLegalSection =
    userSideBarPermissions.isFaqView ||
    userSideBarPermissions.isTermsConditionsView ||
    userSideBarPermissions.isPrivacyPolicyView

  const showLocationsSection =
    userSideBarPermissions.isLocationsView ||
    userSideBarPermissions.isCountriesView

  const showInsightsSection =
    userSideBarPermissions.isReportView ||
    userSideBarPermissions.isEstoreReportView

  const showOrganizationSection =
    userSideBarPermissions.isBrandsManagementView ||
    userSideBarPermissions.isVehicleView

  const showAccessSection =
    userSideBarPermissions.isUsersView ||
    userSideBarPermissions.isUserActivityView ||
    userSideBarPermissions.isRolesView

  const showSettingsDropdown =
    userSideBarPermissions.isReferralView ||
    userSideBarPermissions.isAddEmailView ||
    userSideBarPermissions.isAppSettingsView ||
    userSideBarPermissions.isLoyaltyView ||
    userSideBarPermissions.isWalletSettingsView ||
    userSideBarPermissions.isIngredientView ||
    userSideBarPermissions.isAllergenView ||
    userSideBarPermissions?.MenusUpdateSubPermissions?.clear_menu_cache
      ?.isEnabled ||
    userSideBarPermissions?.AppSettingsViewSubPermissions
      ?.enable_erp_integration?.isEnabled ||
    userSideBarPermissions?.AppSettingsUpdateSubPermissions
      ?.enable_erp_integration?.isEnabled ||
    userSideBarPermissions?.isEidiyahSettingsView ||
    userSideBarPermissions.isOrderCancelSettingsView ||
    userSideBarPermissions.isDeliverySettingsView ||
    userSideBarPermissions.isDeliveryPartnerSettingsView ||
    isSocialAuthSidebarVisible ||
    accountType !== "superadmin"

  const showPosApiKeyNav =
    userSideBarPermissions.isAPIKeyView && showLegacyPosApiKeyTab

  const showPosSettingsSection =
    showPosApiKeyNav ||
    userSideBarPermissions.isBranchSyncView ||
    userSideBarPermissions.isMenuSyncView

  const showSystemSection =
    showSettingsDropdown ||
    showPosSettingsSection ||
    isIntegrationsSidebarVisible ||
    userSideBarPermissions.isApiLogsView

  const isBannersOffersActive =
    normalizedPathname === "/banners" ||
    normalizedPathname.startsWith("/banners/") ||
    normalizedPathname === "/offer-banners" ||
    normalizedPathname.startsWith("/offer-banners/")

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu" className="custom_sidebar_menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            {accountType !== "superadmin" &&
              (userSideBarPermissions.isDashboardEidiyahView ||
                userSideBarPermissions.isDashboardSalesView ||
                userSideBarPermissions.isDashboardBranchesView ||
                userSideBarPermissions.isDashboardRatingsView ||
                userSideBarPermissions.isDashboardBookingsView ||
                userSideBarPermissions.isDashboardEstoreView ||
                userSideBarPermissions.isDashboardMarketingView ||
                userSideBarPermissions.isDashboardWalletView) ? (
              <>
                <li>
                  <Link to="/dashboard">
                    <i className="bx bx-home-circle"></i>
                    <span>{props.t("Dashboard")}</span>
                  </Link>
                </li>
              </>
            ) : null}
            {accountType !== "superadmin" && (
              <>
                {showOrganizationSection ? (
                  <li className="menu-title">{props.t("Organization")}</li>
                ) : null}

                {userSideBarPermissions.isBrandsManagementView ? (
                  <li>
                    <Link to="/brands">
                      <i className="bx bx-store"></i>
                      <span>{props.t("Brands")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isVehicleView ? (
                  <li>
                    <Link to="/vehicles">
                      <i className="bx bxs-car"></i>
                      <span>{props.t("Vehicles")}</span>
                    </Link>
                  </li>
                ) : null}

                {showLocationsSection ? (
                  <li className="menu-title">{props.t("Locations")}</li>
                ) : null}

                {userSideBarPermissions.isCountriesView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bxs-flag-alt "></i>
                      <span>{props.t("Countries")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/countries">{props.t("Countries")}</Link>
                      </li>
                      {userSideBarPermissions.isRegionView ? (
                        <li>
                          <Link to="/regions">{props.t("Regions")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isCityView ? (
                        <li>
                          <Link to="/cities">{props.t("Cities")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isZoneView ? (
                        <li>
                          <Link to="/zones">{props.t("Zones")}</Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions.isLocationsView ? (
                  <li>
                    <Link to="/branches">
                      <i className="bx bx-target-lock"></i>
                      <span>{props.t("Branches")}</span>
                    </Link>
                  </li>
                ) : null}

                {showMarketingSection ? (
                  <li className="menu-title">{props.t("Marketing")}</li>
                ) : null}

                {showBannersOffers ? (
                  <li className={isBannersOffersActive ? "mm-active" : undefined}>
                    <Link
                      to="/#"
                      className={`has-arrow${isBannersOffersActive ? " active" : ""}`}
                      onClick={handleMenuToggleClick}
                    >
                      <i className="bx bx-photo-album"></i>
                      <span>{props.t("Banners & Offers")}</span>
                    </Link>
                    <ul
                      className={`sub-menu${isBannersOffersActive ? " mm-show" : ""}`}
                    >
                      {userSideBarPermissions.isInAppContentView ? (
                        <li>
                          <Link to="/banners">{props.t("Banners")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isOfferBannerView ? (
                        <li>
                          <Link to="/offer-banners">
                            {props.t("Offer Banners")}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {showDiscountsCouponsSection ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-trophy"></i>
                      <span>{props.t("Discounts & Coupons")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {userSideBarPermissions.isPromotionView && (
                        <>
                          <li>
                            <Link to="/discounts">{props.t("Discounts")}</Link>
                          </li>
                          <li>
                            <Link to="/coupons-group">
                              {props.t("Coupons Group")}
                            </Link>
                          </li>
                          <li>
                            <Link to="/coupons">{props.t("Coupons")}</Link>
                          </li>
                          <li>
                            <Link to="/coupon-usage">
                              {props.t("Coupon Usage")}
                            </Link>
                          </li>
                        </>
                      )}
                      {userSideBarPermissions.isCouponRedeemView && (
                        <li>
                          <Link to="/wallet-coupons">
                            {props.t("Wallet Coupons")}
                          </Link>
                        </li>
                      )}
                      {isCouponsCreate && (
                        <li>
                          <Link to="/generate-bulk-coupons">
                            {props.t("Generate Bulk Coupon")}
                          </Link>
                        </li>
                      )}
                    </ul>
                  </li>
                ) : null}

                {showGiftCardSection ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bxs-gift"></i>
                      <span>{props.t("Gift Card")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {userSideBarPermissions.isGiftCardView && (
                        <li>
                          <Link to="/giftcard-categories">
                            {props.t("Gift Categories")}
                          </Link>
                        </li>
                      )}
                      {userSideBarPermissions.isGiftCardView && (
                        <li>
                          <Link to="/giftcard">{props.t("Gift Card List")}</Link>
                        </li>
                      )}
                      {userSideBarPermissions.isGiftCardView && (
                        <li>
                          <Link to="/templates">{props.t("Dynamic Config")}</Link>
                        </li>
                      )}
                      {userSideBarPermissions.isDynamicLinkView && (
                        <li>
                          <Link to="/Giftcard-Deep-Link">
                            {props.t("Gift Card Deep Link")}
                          </Link>
                        </li>
                      )}
                      {userSideBarPermissions.isGiftCardUsageView ? (
                        <li>
                          <Link to="/giftcard-reports">
                            {props.t("Gift Card Reports")}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions.isNotificationView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-bell"></i>
                      <span>{props.t("Notifications")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/app-notifications">
                          {props.t("App Notifications")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/push-notifications">
                          {props.t("Push Notifications")}
                        </Link>
                      </li>
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions.isQrCodeView ? (
                  <li>
                    <Link to="/generate-qr-code">
                      <i className="bx bxs-barcode"></i>
                      <span>{props.t("Generate QR Code")}</span>
                    </Link>
                  </li>
                ) : null}

                {showCatalogSection ? (
                  <li className="menu-title">{props.t("Catalog")}</li>
                ) : null}

                {userSideBarPermissions.isMenusView ||
                userSideBarPermissions.isItemTagView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-food-menu"></i>
                      <span>{props.t("Menus")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {(userSideBarPermissions?.MenusSubPermissions
                        ?.menu_full_access_view?.isEnabled ||
                        isTenantOwnerAdmin()) && (
                          <>
                            <li>
                              <Link to="/menus/menu-setup">
                                {props.t("Menu Setup")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/menus/categories">
                                {props.t("Categories")}
                              </Link>
                            </li>
                          </>
                        )}
                      {(userSideBarPermissions?.MenusSubPermissions
                        ?.item_access_view?.isEnabled ||
                        userSideBarPermissions?.MenusSubPermissions
                          ?.menu_full_access_view?.isEnabled ||
                        isTenantOwnerAdmin()) && (
                          <li>
                            <Link to="/menus/items">{props.t("Items")}</Link>
                          </li>
                        )}
                      {userSideBarPermissions.isItemTagView ? (
                        <li>
                          <Link to="/menus/item-tags">{props.t("Item Tags")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.MenusSubPermissions
                        ?.modifier_access_view?.isEnabled && (
                          <li className={isModifierGroupsActive ? "mm-active" : undefined}>
                            <Link
                              to="/menus/modifier-groups"
                              className={isModifierGroupsActive ? "active" : undefined}
                            >
                              {props.t("Modifier Groups")}
                            </Link>
                          </li>
                        )}
                      {userSideBarPermissions?.MenusSubPermissions
                        ?.modifier_access_view?.isEnabled && (
                          <li>
                            <Link to="/menus/modifiers">{props.t("Modifiers")}</Link>
                          </li>
                        )}
                      {userSideBarPermissions?.MenusSubPermissions
                        ?.combo_access_view?.isEnabled && (
                          <>
                            <li>
                              <Link to="/menus/item-groups">
                                {props.t("Item Groups")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/menus/combos">{props.t("Combos")}</Link>
                            </li>
                          </>
                        )}
                    </ul>
                  </li>
                ) : null}

                {showOperationsSection ? (
                  <li className="menu-title">{props.t("Operations")}</li>
                ) : null}

                {userSideBarPermissions.isOrdersView ? (
                  <li className={isOrdersActive ? "mm-active" : undefined}>
                    {visibleOrderBrands.length >= 2 ? (
                      <>
                        <Link
                          to="/#"
                          className={`has-arrow${isOrdersActive ? " active" : ""}`}
                          onClick={handleMenuToggleClick}
                        >
                          <i className="bx bx-receipt"></i>
                          <span>{props.t("Orders")}</span>
                        </Link>
                        <ul className={`sub-menu${isOrdersActive ? " mm-show" : ""}`}>
                          {visibleOrderBrands.map(brand => {
                            const brandPath = getBrandOrdersBasePath(brand.brandId)
                            const isBrandActive =
                              normalizedPathname === brandPath ||
                              normalizedPathname.startsWith(`${brandPath}/`)
                            return (
                              <li
                                key={brand.brandId}
                                className={isBrandActive ? "mm-active" : undefined}
                              >
                                <Link
                                  to={brandPath}
                                  className={isBrandActive ? "active" : undefined}
                                >
                                  {brand.displayName || brand.brandName}
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      </>
                    ) : (
                      <Link
                        to={
                          visibleOrderBrands.length === 1
                            ? getBrandOrdersBasePath(
                                visibleOrderBrands[0].brandId
                              )
                            : "/orders"
                        }
                        className={isOrdersActive ? "active" : undefined}
                      >
                        <i className="bx bx-receipt"></i>
                        <span>{props.t("Orders")}</span>
                      </Link>
                    )}
                  </li>
                ) : null}

                {userSideBarPermissions.isBookingView ? (
                  <li>
                    <Link to="/bookings">
                      <i className="bx bx-calendar"></i>
                      <span>{props.t("Bookings")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isDeliveryPartnersView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-cycling"></i>
                      <span>{props.t("Delivery Management")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/delivery-partners">
                          {props.t("Riders")}
                        </Link>
                      </li>
                    </ul>
                  </li>
                ) : null}

                {showAccessSection ? (
                  <li className="menu-title">{props.t("Access")}</li>
                ) : null}

                {userSideBarPermissions.isRolesView ? (
                  <li>
                    <Link to="/roles">
                      <i className="bx bx-briefcase"></i>
                      <span>{props.t("Roles Management")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isUsersView ? (
                  <li>
                    <Link to="/users">
                      <i className="bx bx-user"></i>
                      <span>{props.t("User Management")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions?.isUserActivityView ? (
                  <li>
                    <Link to="/user-activity">
                      <i className=" bx bx-list-check"></i>
                      <span>{props.t("User Activity")}</span>
                    </Link>
                  </li>
                ) : null}

                {showCustomersSupportSection ? (
                  <li className="menu-title">
                    {props.t("Customers & Support")}
                  </li>
                ) : null}

                {userSideBarPermissions.isCustomersView ||
                userSideBarPermissions.isCustomersDeleted ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-group"></i>
                      <span>{props.t("Customers")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {userSideBarPermissions.isCustomersView && (
                        <li>
                          <Link to="/customers">{props.t("Customers")}</Link>
                        </li>
                      )}
                      {userSideBarPermissions.isCustomersDeleted && (
                        <li>
                          <Link to="/customers/deleted-customers">
                            {props.t("Deleted Customers")}
                          </Link>
                        </li>
                      )}

                      {userSideBarPermissions.isCustomersView &&
                        userSideBarPermissions?.customerSubPermissions
                          ?.view_loyalty_points?.isEnabled && (
                          <li>
                            <Link to="/customers/loyalty">
                              {props.t("Loyalty")}
                            </Link>
                          </li>
                        )}
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions.isCustomerSupportView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-support"></i>
                      <span>{props.t("Customer Support")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/support-issues">
                          {props.t("Support Issues")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/support-tickets">
                          {props.t("Support Tickets")}
                        </Link>
                      </li>
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions.isRatingView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-star"></i>
                      <span>{props.t("Ratings")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/rating-questions">
                          {props.t("Rating Questions")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/branch-ratings">
                          {props.t("Branch Ratings")}
                        </Link>
                      </li>
                    </ul>
                  </li>
                ) : null}

                {showHelpLegalSection ? (
                  <li className="menu-title">{props.t("Help & Legal")}</li>
                ) : null}

                {userSideBarPermissions.isFaqView ? (
                  <li>
                    <Link to="/faq">
                      <i className="bx bx-help-circle"></i>
                      <span>{props.t("FAQ")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isTermsConditionsView ? (
                  <li>
                    <Link to="/terms-and-conditions">
                      <i className="bx bx-file"></i>
                      <span>{props.t("Terms And Conditions")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isPrivacyPolicyView ? (
                  <li>
                    <Link to="/privacy-policy">
                      <i className="bx bx-shield-quarter"></i>
                      <span>{props.t("Privacy Policy")}</span>
                    </Link>
                  </li>
                ) : null}

                {showInsightsSection ? (
                  <li className="menu-title">{props.t("Insights")}</li>
                ) : null}

                {userSideBarPermissions.isReportView ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-bar-chart"></i>
                      <span>{props.t("Reports")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_sales_reports?.isEnabled ? (
                        <li>
                          <Link to="/sales-reports">{props.t("Sales Reports")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_wallet_reports?.isEnabled ? (
                        <li>
                          <Link to="/wallet-reports">
                            {props.t("Wallet Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_wallet_transfer_reports?.isEnabled ? (
                        <li>
                          <Link to="/wallet-transfer-reports">
                            {props.t("Wallet Transfer Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_wallet_purchase_reports?.isEnabled ? (
                        <li>
                          <Link to="/wallet-purchase-reports">
                            {props.t("Wallet Purchase Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_premium_gift_card_reports?.isEnabled ? (
                        <li>
                          <Link to="/premium-gift-card-reports">
                            {props.t("Premium Gift Card Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_payments_reports?.isEnabled ? (
                        <li>
                          <Link to="/payments-reports">
                            {props.t("Payments Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_business_tax_reports?.isEnabled ? (
                        <li>
                          <Link to="/business-tax-reports">
                            {props.t("Business Tax Reports")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.reportViewSubPermissions
                        ?.view_loyalty_points_reports?.isEnabled ? (
                        <li>
                          <Link to="/loyalty-points-reports">
                            {props.t("Loyalty Points Reports")}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {userSideBarPermissions?.isEstoreReportView && (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-bar-chart"></i>
                      <span>{props.t("E-Store Reports")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {userSideBarPermissions?.isEstoreReportView &&
                        userSideBarPermissions?.eStoreViewSubPermissions
                          ?.view_sales_by_product?.isEnabled ? (
                        <li>
                          <Link to="/e-store/sales-report-by-product">
                            {props.t("Sales Report By Product")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.isEstoreReportView &&
                        userSideBarPermissions?.eStoreViewSubPermissions
                          ?.view_summary_sales?.isEnabled ? (
                        <li>
                          <Link to="/e-store/summary-sales-report">
                            {props.t("Summary Sales Report")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions?.isEstoreReportView &&
                        userSideBarPermissions?.eStoreViewSubPermissions
                          ?.view_sales_orders_details?.isEnabled ? (
                        <li>
                          <Link to="/e-store/sales-order-details">
                            {props.t("Sales Order Details")}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                )}

                {showSystemSection ? (
                  <li className="menu-title">{props.t("System")}</li>
                ) : null}

                {showSettingsDropdown ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx bx-cog"></i>
                      <span>{props.t("Settings")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/my-preference">{props.t("My Preference")}</Link>
                      </li>
                      {userSideBarPermissions.isAppSettingsView ||
                        userSideBarPermissions?.MenusUpdateSubPermissions
                          ?.clear_menu_cache?.isEnabled ||
                        userSideBarPermissions?.AppSettingsViewSubPermissions
                          ?.enable_erp_integration?.isEnabled ||
                        userSideBarPermissions?.AppSettingsUpdateSubPermissions
                          ?.enable_erp_integration?.isEnabled ? (
                        <li>
                          <Link to="/app-settings">{props.t("App Settings")}</Link>
                        </li>
                      ) : null}

                      {userSideBarPermissions?.isEidiyahSettingsView && (
                        <li>
                          <Link to="/giftcard-settings">
                            {props.t("Gift Card Settings")}
                          </Link>
                        </li>
                      )}

                      {userSideBarPermissions.isAddEmailView ? (
                        <li>
                          <Link to="/add-emails">{props.t("Add Emails")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isReferralView ? (
                        <li>
                          <Link to="/referral-settings">
                            {props.t("Referral Settings")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isLoyaltyView ? (
                        <li>
                          <Link to="/loyalty-settings">
                            {props.t("Loyalty Settings")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isWalletSettingsView ? (
                        <li>
                          <Link to="/wallet-settings">
                            {props.t("Wallet Settings")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isOrderCancelSettingsView ? (
                        <li>
                          <Link to="/order-cancel-settings">
                            {props.t("Order Cancel Settings")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isDeliverySettingsView ? (
                        <li>
                          <Link to="/delivery-setting">
                            {props.t("Delivery Setting")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isDeliveryPartnerSettingsView ? (
                        <li>
                          <Link to="/delivery-partner-settings">
                            {props.t("Delivery Partner Settings")}
                          </Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isIngredientView ? (
                        <li>
                          <Link to="/ingredients">{props.t("Ingredients")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isAllergenView ? (
                        <li>
                          <Link to="/allergens">{props.t("Allergens")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isStaticMaintenanceView ? (
                        <li>
                          <Link to="/static-maintenance">
                            {props.t("Static Maintenance")}
                          </Link>
                        </li>
                      ) : null}
                      {isSocialAuthSidebarVisible ? (
                        <li>
                          <Link to="/settings/social-auth">
                            {props.t("Social Login")}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {showPosSettingsSection ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-slider"></i>
                      <span>{props.t("POS Settings")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {showPosApiKeyNav ? (
                        <li>
                          <Link to="/api-key">{props.t("API key")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isBranchSyncView ? (
                        <li>
                          <Link to="/branch-sync">{props.t("Branch Sync")}</Link>
                        </li>
                      ) : null}
                      {userSideBarPermissions.isMenuSyncView ? (
                        <li>
                          <Link to="/menu-sync">{props.t("Menu Sync")}</Link>
                        </li>
                      ) : null}
                    </ul>
                  </li>
                ) : null}

                {isIntegrationsSidebarVisible ? (
                  <li>
                    <Link to="/integrations">
                      <i className="bx bx-plug"></i>
                      <span>{props.t("Integrations")}</span>
                    </Link>
                  </li>
                ) : null}

                {userSideBarPermissions.isApiLogsView ? (
                  <li>
                    <Link to="/api-logs">
                      <i className="bx bx-menu"></i>
                      <span>{props.t("API Logs")}</span>
                    </Link>
                  </li>
                ) : null}

                {showBillingNav ? (
                  <li className="menu-title">{props.t("Account")}</li>
                ) : null}

                {showBillingNav ? (
                  <li>
                    <Link to="/#" className="has-arrow" onClick={handleMenuToggleClick}>
                      <i className="bx bx-credit-card"></i>
                      <span>{props.t("Billing")}</span>
                    </Link>
                    <ul className="sub-menu">
                      <li>
                        <Link to="/billing">{props.t("Overview")}</Link>
                      </li>
                      <li>
                        <Link to="/billing/plans">{props.t("Plans")}</Link>
                      </li>
                      <li>
                        <Link to="/billing/invoices">{props.t("Invoices")}</Link>
                      </li>
                    </ul>
                  </li>
                ) : null}
              </>
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
